import React, { useEffect, useMemo, useState, Suspense } from 'react';
import { apiFetch } from '../utils/apiFetch';
import { useNavigate } from 'react-router-dom';
import MovdNotice from '../design-system/MovdNotice.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const ReviewForm = React.lazy(() => import('../components/ReviewForm.jsx'));

const statusCopy = {
  pending: 'Waiting for mover acceptance',
  confirmed: 'Mover accepted your move',
  in_progress: 'Your move is in progress',
  awaiting_customer_confirmation: 'Action needed: confirm your move',
  completed: 'Move completed',
  cancelled: 'Booking cancelled',
  refunded: 'Payment refunded',
  payment_failed: 'Payment needs attention',
};

const activeStatuses = new Set(['pending', 'confirmed', 'in_progress', 'awaiting_customer_confirmation']);

function Progress({ status }) {
  const steps = ['Booking accepted', 'Mover assigned', 'Moving day', 'Move complete'];
  const progress = { pending: 0, confirmed: 1, in_progress: 2, awaiting_customer_confirmation: 3, completed: 4 }[status] || 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, width: '100%' }}>
      {steps.map((step, index) => (
        <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, padding: 6, borderRadius: 6, background: index === progress ? 'rgba(37,99,235,0.12)' : 'transparent' }}>
          <span aria-hidden="true" style={{ flexShrink: 0, color: index < progress ? '#16A34A' : index === progress ? '#2563EB' : '#CBD5E1', fontWeight: 900 }}>●</span>
          <span style={{ fontSize: 12, fontWeight: index <= progress ? 800 : 500, color: index <= progress ? '#0F172A' : movdTokens.color.content.secondary, lineHeight: 1.2 }}>{step}</span>
        </div>
      ))}
    </div>
  );
}

const UserDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    apiFetch('/api/customer/bookings', { credentials: 'include' })
      .then(async res => {
        if (!res.ok) throw new Error('Failed to load bookings');
        setBookings(await res.json());
      })
      .catch(() => setSnackbar({ open: true, message: 'Failed to load your bookings.', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!snackbar.open) return undefined;
    const timer = setTimeout(() => setSnackbar(s => ({ ...s, open: false })), 2000);
    return () => clearTimeout(timer);
  }, [snackbar.open]);

  const handleReviewClick = (booking) => {
    setSelectedBooking(booking);
    setReviewOpen(true);
  };

  const currentBooking = useMemo(() => bookings.find(booking => activeStatuses.has(booking.status)) || bookings[0], [bookings]);
  const pastBookings = useMemo(() => bookings.filter(booking => booking._id !== currentBooking?._id), [bookings, currentBooking]);
  const nextAction = currentBooking?.status === 'awaiting_customer_confirmation' && currentBooking.payment?._id
    ? { label: 'Confirm completed move', action: () => navigate(`/move-complete/${currentBooking.payment._id}`) }
    : { label: 'Open move workspace', action: () => navigate(`/customer-portal/booking/${currentBooking?._id}`) };

  return (
    <main id="main-content" role="main" style={{ background: '#F1F5F9', color: movdTokens.color.content.primary, minHeight: '100vh', padding: 'clamp(16px, 4vw, 40px)' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <p style={{ margin: 0, color: '#2563EB', fontWeight: 800, letterSpacing: '.12em', fontSize: 11, textTransform: 'uppercase' }}>Customer portal</p>
        <h1 style={{ margin: '4px 0 6px', color: '#0F172A', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3rem)' }}>Your move, at a glance.</h1>
        <p style={{ color: movdTokens.color.content.secondary, marginBottom: 28 }}>Everything you need for your current move, from booking to completion.</p>

        {loading ? (
          <div style={{ height: 340, borderRadius: 12, background: '#E2E8F0' }} />
        ) : bookings.length === 0 ? (
          <div role="status" style={{ background: '#fff', border: '1px solid #D9E2EC', borderRadius: 10, padding: 'clamp(24px, 6vw, 48px)', textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 850 }}>Your next move starts here.</h2>
            <p style={{ color: movdTokens.color.content.secondary, maxWidth: 520, margin: '8px auto 0' }}>Once you book a mover, this is where MOVD will keep your status, messages, payment, and move-day actions together.</p>
            <MovdButton onClick={() => navigate('/')} style={{ marginTop: 20 }}>Start a quote</MovdButton>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 24 }}>
            <div style={{ background: movdTokens.color.brand.ink, color: '#fff', borderRadius: 12, padding: 'clamp(20px, 4vw, 32px)', boxShadow: '0 18px 42px rgba(15,23,42,0.16)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ margin: 0, color: '#93C5FD', fontWeight: 800, letterSpacing: '.1em', fontSize: 11, textTransform: 'uppercase' }}>Current move</p>
                  <h2 style={{ margin: '4px 0 0', fontWeight: 900, fontSize: 24 }}>{statusCopy[currentBooking.status] || currentBooking.status}</h2>
                </div>
                <span style={{ background: 'rgba(255,255,255,.1)', borderRadius: 20, padding: '4px 12px', fontWeight: 700, fontSize: 12, alignSelf: 'flex-start' }}>{currentBooking.jobNumber ? `Booking ${currentBooking.jobNumber}` : 'MOVD booking'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 24px minmax(0,1fr)', alignItems: 'center', gap: 16, margin: '24px 0' }}>
                <div><p style={{ margin: 0, color: '#93C5FD', fontWeight: 800, fontSize: 11 }}>PICKUP</p><p style={{ margin: 0, fontWeight: 800 }}>{currentBooking.origin}</p></div>
                <span aria-hidden="true" style={{ color: '#60A5FA', textAlign: 'center' }}>→</span>
                <div><p style={{ margin: 0, color: '#93C5FD', fontWeight: 800, fontSize: 11 }}>DROP-OFF</p><p style={{ margin: 0, fontWeight: 800 }}>{currentBooking.destination}</p></div>
              </div>
              <Progress status={currentBooking.status} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
              <div style={{ background: '#fff', border: '1px solid #D9E2EC', borderRadius: 10, padding: 'clamp(20px, 3vw, 24px)' }}>
                <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Next up</p>
                <h3 style={{ margin: '8px 0 0', fontSize: 20, fontWeight: 850 }}>{nextAction.label}</h3>
                <p style={{ color: movdTokens.color.content.secondary, margin: '6px 0 0' }}>Your move details, communication, payment, and completion actions live in one workspace.</p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
                  <MovdButton onClick={nextAction.action}>{nextAction.label}</MovdButton>
                  <MovdButton variant="secondary" onClick={() => navigate(`/customer-portal/booking/${currentBooking._id}`)}>Open conversation</MovdButton>
                  {['completed', 'awaiting_customer_confirmation'].includes(currentBooking.status) && <MovdButton variant="ghost" onClick={() => handleReviewClick(currentBooking)}>Leave review</MovdButton>}
                </div>
              </div>
              <div style={{ background: '#fff', border: '1px solid #D9E2EC', borderRadius: 10, padding: 'clamp(20px, 3vw, 24px)' }}>
                <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Move snapshot</p>
                <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                  <div><p style={{ margin: 0, fontSize: 12, color: movdTokens.color.content.secondary }}>MOVE DATE</p><p style={{ margin: 0, fontWeight: 700 }}>{new Date(currentBooking.moveDate).toLocaleDateString()}</p></div>
                  <div><p style={{ margin: 0, fontSize: 12, color: movdTokens.color.content.secondary }}>ARRIVAL WINDOW</p><p style={{ margin: 0, fontWeight: 700 }}>{currentBooking.timeWindow || 'To be confirmed'}</p></div>
                  <div><p style={{ margin: 0, fontSize: 12, color: movdTokens.color.content.secondary }}>PAYMENT</p><p style={{ margin: 0, fontWeight: 700 }}>{currentBooking.payment?.paymentStatus || 'Pending'}</p></div>
                </div>
              </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #D9E2EC', borderRadius: 10, padding: 'clamp(20px, 3vw, 24px)' }}>
              <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Your mover</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#2563EB', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, overflow: 'hidden', flexShrink: 0 }}>
                  {currentBooking.mover?.logo || currentBooking.mover?.profileImage ? <img src={currentBooking.mover.logo || currentBooking.mover.profileImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (currentBooking.mover?.name?.charAt(0) || 'M')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 850 }}>{currentBooking.mover?.name || 'Your moving company'}</p>
                  <p style={{ margin: 0, fontSize: 13, color: movdTokens.color.content.secondary }}>Your mover will coordinate the next details with you.</p>
                </div>
                <MovdButton variant="ghost" onClick={() => navigate(`/customer-portal/booking/${currentBooking._id}`)}>View workspace</MovdButton>
              </div>
            </div>

            {pastBookings.length > 0 && (
              <div>
                <div style={{ height: 1, background: '#E2E8F0', marginBottom: 20 }} />
                <p style={{ margin: 0, color: movdTokens.color.content.secondary, fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Past bookings</p>
                <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                  {pastBookings.map(booking => (
                    <div key={booking._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', padding: 12, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: 700 }}>{booking.mover?.name || 'MOVD mover'}</p>
                        <p style={{ margin: 0, fontSize: 12, color: movdTokens.color.content.secondary }}>{new Date(booking.moveDate).toLocaleDateString()} · {statusCopy[booking.status] || booking.status}</p>
                      </div>
                      <button type="button" onClick={() => navigate(`/customer-portal/booking/${booking._id}`)} style={{ background: 'transparent', border: 'none', color: movdTokens.color.action.primary, fontWeight: 700, cursor: 'pointer' }}>View</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {snackbar.open && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, minWidth: 280 }}>
            <MovdNotice tone={snackbar.severity}>{snackbar.message}</MovdNotice>
          </div>
        )}
        <Suspense fallback={<div>Loading review form...</div>}>
          <ReviewForm
            open={reviewOpen}
            onClose={() => setReviewOpen(false)}
            bookingId={selectedBooking?._id}
            moverId={selectedBooking?.mover?._id}
            onSubmitted={() => setSnackbar({ open: true, message: 'Thank you for your review!', severity: 'success' })}
          />
        </Suspense>
      </div>
    </main>
  );
};

export default UserDashboard;
